'use client';

import { Member } from '@/app/(intakeFormV2)/intake-v2/types';
import { ActionButton } from '@/components/ui/ActionButton';
import { BackToTop } from '@/components/ui/BackToTop';
import { validateDateOfBirth } from '@/utils/validateDateOfBirth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntakeWizardContext } from '../../contexts/WizardProvider';
import { useWizardNavigation } from '../../hooks/useWizardNavigation';
import { MemberDetailsForm } from '../common/MemberDetailsForm';

const toISODate = (dob: string): string => {
  const match = dob.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return dob;
  const [, month, day, year] = match;
  return `${year}-${month}-${day}`;
};

const toDisplayDate = (dob: string): string => {
  if (!dob) return '';
  const match = dob.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return dob;
  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
};

export const MemberDetailsStep = () => {
  const { state, dispatch } = useIntakeWizardContext();
  const { goToNextStep, goToPreviousStep, hasPreviousStep } =
    useWizardNavigation();

  const pendingNavigation = useRef(false);

  // Navigate after state has been updated and re-rendered
  useEffect(() => {
    if (pendingNavigation.current) {
      pendingNavigation.current = false;

      goToNextStep();
    }
  }, [goToNextStep]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const { signup_type: signupType } = state.formData;

  const extraBlankPerson: undefined | Member = useMemo(() => {
    if (signupType !== 'Myself and my family') return;

    return {
      id: crypto.randomUUID(),
      first_name: '',
      last_name: '',
      date_of_birth: '',
      isPrimary: false,
      plan: 'Signature',
      contact_id: crypto.randomUUID(),
    };
  }, [signupType]);

  // Initialize members from wizard state (session is already loaded at this point)
  // Convert ISO dates (YYYY-MM-DD) back to display format (MM/DD/YYYY)
  const [members, setMembers] = useState<Member[]>(() => {
    if (state.formData.members && state.formData.members?.length > 0) {
      const _members = state.formData.members.map(member => ({
        ...member,
        date_of_birth: toDisplayDate(member.date_of_birth),
      }));

      if (extraBlankPerson && _members.length === 1) {
        _members.push(extraBlankPerson);
      }

      return _members;
    }

    const shouldPrefill =
      signupType === 'Myself' || signupType === 'Myself and my family';

    const contact = shouldPrefill ? state.formData?.contact_details : undefined;

    const _members: Member[] = [
      {
        id: crypto.randomUUID(),
        first_name: contact?.first_name || '',
        last_name: contact?.last_name || '',
        date_of_birth: '',
        isPrimary: true,
        plan: 'Signature' as const,
        contact_id: crypto.randomUUID(),
      },
    ];

    if (extraBlankPerson) {
      _members.push(extraBlankPerson);
    }

    return _members;
  });

  const handleMembersChange = useCallback((newMembers: Member[]) => {
    setMembers(newMembers);
    // Clear errors for fields that may have changed

    setErrors(prev => {
      const next = { ...prev };
      // Remove member-specific errors that may be stale
      Object.keys(next).forEach(key => {
        if (key.startsWith('member_')) {
          delete next[key];
        }
      });

      return next;
    });
  }, []);

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (members.length === 0) {
      newErrors.members = 'At least one member is required';
    }

    members.forEach((member, index) => {
      if (!member.first_name.trim()) {
        newErrors[`member_${index}_first_name`] = 'First name is required';
      }
      if (!member.last_name.trim()) {
        newErrors[`member_${index}_last_name`] = 'Last name is required';
      }
      const dobError = validateDateOfBirth(member.date_of_birth);
      if (dobError) {
        newErrors[`member_${index}_date_of_birth`] = dobError;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [members]);

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();

      if (!validateForm()) {
        return;
      }

      const membersForStorage = members.map(member => ({
        ...member,
        date_of_birth: toISODate(member.date_of_birth),
      }));

      dispatch({
        type: 'SET_FORM_DATA',
        payload: {
          members: membersForStorage,
          additional_members: membersForStorage.length > 1,
          number_of_members: membersForStorage.length,
        },
      });

      pendingNavigation.current = true;
    },
    [validateForm, members, dispatch]
  );

  return (
    <div className='mx-auto w-full max-w-[800px] px-4 py-8'>
      <form onSubmit={handleSubmit} className='flex flex-col gap-[40px]'>
        <div className='flex flex-col items-center gap-[24px] text-center xl:gap-[40px]'>
          <h1 className='font-serif text-[27px] leading-[115%] font-light text-[#292722] xl:text-[36px] xl:leading-[130%]'>
            Enter the details for each member you want to sign up
          </h1>

          <p className='font-sans text-[18px] leading-[130%] font-normal text-[#292722] xl:text-[21px]'>
            Birth dates are used to finalize your custom quote pricing.
          </p>
        </div>

        {errors.members && (
          <p className='text-sm text-red-500'>{errors.members}</p>
        )}

        <MemberDetailsForm
          value={members}
          onChange={handleMembersChange}
          errors={errors}
        />

        <div className='flex flex-col gap-[16px] md:flex-row md:gap-[48px]'>
          <ActionButton
            label='Back'
            variant='secondary'
            type='button'
            onClick={goToPreviousStep}
            disabled={!hasPreviousStep}
            className='hidden flex-1 md:order-1 md:block'
          />

          <ActionButton
            label='Save'
            variant='primary'
            type='submit'
            className='w-full flex-1 md:order-2 md:w-auto'
          />
        </div>

        <BackToTop />
      </form>
    </div>
  );
};
